import { GatewayQuestionnaire } from "@conformite/gateway";
import { useController } from "@endya-conformite/ui-kit/src/lya/react-hook-form";
import IaSparkle from "@endya-conformite/ui-kit/src/assets/icons/ia-sparkle.svg?react";
import { parseAnswerValue, serializeAnswerValue } from "../answerValue";
import styles from "./QuestionnaireColoredButtonGroup.module.scss";

type QuestionnaireColoredButtonGroupProps = {
  name: string;
  label: string;
  helpText?: string;
  required?: boolean;
  options: GatewayQuestionnaire.QuestionOption[];
  disabled?: boolean;
  multiple?: boolean;
  colorOverrides?: Record<string, GatewayQuestionnaire.OptionColor>;
  suggestedOptionLabel?: string;
};

export function QuestionnaireColoredButtonGroup({
  name,
  label,
  helpText,
  required,
  options,
  disabled,
  multiple,
  colorOverrides,
  suggestedOptionLabel,
}: QuestionnaireColoredButtonGroupProps) {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    rules: required
      ? {
          validate: (v: string) =>
            multiple
              ? parseAnswerValue(v).length > 0 || "Ce champ est obligatoire"
              : Boolean(v) || "Ce champ est obligatoire",
        }
      : undefined,
  });

  const rawValue = (field.value as string | undefined) ?? "";
  let selectedIds: string[];
  if (multiple) {
    selectedIds = parseAnswerValue(rawValue);
  } else if (rawValue) {
    selectedIds = [rawValue];
  } else {
    selectedIds = [];
  }

  const handleClick = (optId: string) => {
    if (disabled) return;
    if (multiple) {
      const current = new Set(selectedIds);
      if (current.has(optId)) current.delete(optId);
      else current.add(optId);
      field.onChange(serializeAnswerValue(Array.from(current)));
    } else {
      field.onChange(rawValue === optId ? "" : optId);
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.labelWrapper}>
        <span className={styles.label}>
          {label}
          {required && <span className={styles.asterisk}> *</span>}
        </span>
        {helpText && <p className={styles.helpText}>{helpText}</p>}
      </div>
      <div className={styles.group} role="group" aria-label={label}>
        {options.map((opt) => {
          const isSelected = selectedIds.includes(opt.id);
          const isSuggested =
            !!suggestedOptionLabel && opt.label === suggestedOptionLabel;
          return (
            <button
              key={opt.id}
              type="button"
              className={styles.btn}
              data-selected={isSelected ? "true" : "false"}
              data-suggested={isSuggested ? "true" : "false"}
              data-color={colorOverrides?.[opt.id] ?? opt.color ?? "default"}
              disabled={disabled}
              onClick={() => handleClick(opt.id)}
            >
              {opt.label}
              {isSuggested && (
                <IaSparkle
                  className={styles.aiIcon}
                  aria-label="Suggéré par l'IA"
                />
              )}
            </button>
          );
        })}
      </div>
      {error && <span className={styles.error}>{error.message}</span>}
    </div>
  );
}
